import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { SpecEditorContext } from './SpecEditorContext';
import { kindEntry, KINDS_WITH_DATE_RANGE } from './specSchema';
import { countSaveBlockers, countLockErrors } from './validate';
import { Chip, MicroLabel, PassiveCard } from './styles';

// The wiring hub, rendered as a row the operator can read.
//
// This is the single highest-value piece of the design and it earns its space by being wired to
// two other controls, not by being decorative: the Provides list here is the SAME list that
// disables document kinds in WhatPanel and the SAME list that populates the anchor select in
// DateRangeControl. That one wire makes three of the loader's cross-field failures unreachable
// from the form — the axis-REQUIRES-vs-Provides failure, the anchor-names-an-unprovided-field
// failure, and (because the date control derives its mode from its tokens) the anchor-present-
// without-a-T-token failure.
//
// The output-shape line is deliberately the quiet one. Without an `output_schema` it says the
// shape is unverifiable rather than showing a green tick, because a satisfied-looking check that
// guarantees nothing is worse than no check.
//
// It also carries the third state the API added: BOTH contracts declared. That one is not a compile
// failure — the loader tolerates it — but create and PATCH reject it, so it is the only chip here
// that names the SAVE door rather than the lock door.

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

export const ContractRail = () => {
  const { definition, provides, problems } = React.useContext(SpecEditorContext);

  const d = definition || {};
  const trigger = d.trigger || {};
  const documentSet = d.document_set || {};
  const documents = documentSet.documents || {};
  const output = d.output_extraction || {};

  const documentsKind = documents.kind;
  const requires = documentsKind ? kindEntry('documents', documentsKind) : null;
  const requiredFields = (requires && requires.requires) || [];
  const unmet = requiredFields.filter((r) => provides.indexOf(r) === -1);

  const range = KINDS_WITH_DATE_RANGE.indexOf(documentsKind) > -1 ? documents.date_range : null;
  const anchor = range && range.anchor;
  const anchorField = anchor ? String(anchor).replace(/^trigger\./, '') : null;
  const anchorOk = anchorField ? provides.indexOf(anchorField) > -1 : null;

  const schemaDeclared = output.output_schema !== undefined && output.output_schema !== null;
  const formatPrompt = (d.score_gen || {}).format_prompt;
  const proseDeclared = typeof formatPrompt === 'string' && formatPrompt.trim() !== '';
  const saveBlockers = countSaveBlockers(problems);
  const lockErrors = countLockErrors(problems);

  return (
    <PassiveCard aria-label="Stage contracts">
      <MicroLabel>Contracts between the stages</MicroLabel>

      <Container top={8}>
        <Line>
          <Body7 isLowContrast>
            {trigger.kind ? `${trigger.kind} provides` : 'No trigger picked, so nothing is provided'}
          </Body7>
          {provides.map((field) => (
            <Chip key={field} $tone="good">{`trigger.${field}`}</Chip>
          ))}
        </Line>
      </Container>

      <Container top={8}>
        <Line>
          {documentsKind ? (
            <Chip $tone={unmet.length ? 'bad' : 'good'}>
              {requiredFields.length
                ? `documents.${documentsKind} needs ${requiredFields.join(', ')} ${unmet.length ? '✗' : '✓'}`
                : `documents.${documentsKind} needs nothing from the trigger ✓`}
            </Chip>
          ) : (
            <Chip $tone="bad">no documents kind picked</Chip>
          )}

          {range ? (
            <Chip $tone={anchorOk === false ? 'bad' : anchorOk ? 'good' : undefined}>
              {anchor
                ? `date_range.anchor = ${anchor} ${anchorOk ? '✓' : '✗ not provided'}`
                : 'date_range is not anchored'}
            </Chip>
          ) : null}
        </Line>
      </Container>

      <Container top={8}>
        <Line>
          {schemaDeclared && proseDeclared ? (
            <Chip $tone="bad">output contract declared twice — schema AND format_prompt</Chip>
          ) : schemaDeclared ? (
            <Chip $tone="good">output_schema declared — score paths are checked against it</Chip>
          ) : proseDeclared ? (
            <Chip $tone="bad">format_prompt only — a deprecated prose contract</Chip>
          ) : (
            <Chip $tone="bad">no output contract declared</Chip>
          )}
          {schemaDeclared && proseDeclared && (
            <Body7 isLowContrast>
              The loader accepts both, so a spec like this can be running right now — but create and PATCH reject it, so
              this draft cannot be saved until one of them goes. Question 4 has the choice.
            </Body7>
          )}
          {proseDeclared && !schemaDeclared && (
            <Body7 isLowContrast>
              The shape is requested in prose, so no score path can be verified here. The compile will not catch a
              mismatch either — only a real run will. Question 4 converts it to a schema, which is checkable.
            </Body7>
          )}
        </Line>
      </Container>

      <Container top={16}>
        <Body7 isLowContrast>
          {saveBlockers === 0 && lockErrors === 0
            ? 'Nothing here contradicts the rules this editor knows about.'
            : [
                saveBlockers > 0 &&
                  `${saveBlockers} thing${saveBlockers === 1 ? '' : 's'} below would be rejected on save.`,
                lockErrors > 0 &&
                  `${lockErrors} thing${lockErrors === 1 ? '' : 's'} below would fail the compile at lock.`,
              ]
                .filter(Boolean)
                .join(' ')}
        </Body7>
      </Container>
    </PassiveCard>
  );
};
